import apiClient from './apiClient';
import {
  Lead,
  LeadStatus,
  LeadListResponse,
  LeadUpdateResponse,
  Promotion,
  PromotionDetailResponse,
  PromotionListResponse,
  PromotionSurface,
  SubmitLeadPayload,
} from '../types/promotion';

const API_TYPE = 'api' as const;

class PromotionsService {

  /** Public: get the active promotion for a tenant + surface (or null). */
  async getActivePromotion(tenant: string, surface: PromotionSurface): Promise<Promotion | null> {
    try {
      const data = await apiClient.get<{ success: boolean; data: Promotion } | undefined>(
        `/promotions/active?tenant=${encodeURIComponent(tenant)}&surface=${encodeURIComponent(surface)}`,
        undefined,
        API_TYPE,
      );
      if (!data || !data.success) return null;
      return data.data;
    } catch (err: any) {
      if (err?.response?.status === 204 || err?.response?.status === 404) return null;
      // Don't throw — silent fallback so a bad promo config never breaks the page
      console.warn('getActivePromotion failed:', err?.message);
      return null;
    }
  }

  /** Public: submit a lead capture against an active promotion. */
  async submitLead(promoId: string, payload: SubmitLeadPayload): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post<{ success: boolean; error?: string }>(
        `/promotions/${encodeURIComponent(promoId)}/leads`,
        payload,
        undefined,
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to submit');
      }
      return response;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || err?.message || 'Failed to submit');
    }
  }

  /** Admin: list all promotions. */
  async listPromotions(): Promise<Promotion[]> {
    const response = await apiClient.get<PromotionListResponse>(
      '/admin/promotions', undefined, API_TYPE,
    );
    if (!response.success) throw new Error('Failed to load promotions');
    return response.data;
  }

  /** Admin: get a single promotion (with lead_count). */
  async getPromotion(promoId: string): Promise<Promotion> {
    const response = await apiClient.get<PromotionDetailResponse>(
      `/admin/promotions/${encodeURIComponent(promoId)}`, undefined, API_TYPE,
    );
    if (!response.success) throw new Error('Failed to load promotion');
    return response.data;
  }

  /** Admin: create a promotion. */
  async createPromotion(payload: Promotion): Promise<Promotion> {
    const response = await apiClient.post<PromotionDetailResponse>(
      '/admin/promotions', payload, undefined, API_TYPE,
    );
    if (!response.success) throw new Error('Failed to create promotion');
    return response.data;
  }

  /** Admin: update a promotion. */
  async updatePromotion(promoId: string, payload: Promotion): Promise<Promotion> {
    const response = await apiClient.put<PromotionDetailResponse>(
      `/admin/promotions/${encodeURIComponent(promoId)}`, payload, undefined, API_TYPE,
    );
    if (!response.success) throw new Error('Failed to update promotion');
    return response.data;
  }

  /** Admin: delete a promotion (only allowed when lead_count == 0). */
  async deletePromotion(promoId: string): Promise<void> {
    await apiClient.delete<{ success: boolean }>(
      `/admin/promotions/${encodeURIComponent(promoId)}`, undefined, API_TYPE,
    );
  }

  /** Admin: list leads for a promotion. */
  async listLeads(
    promoId: string,
    filters: { status?: LeadStatus; search?: string; cursor?: string } = {},
  ): Promise<{ leads: Lead[]; cursor: string | null }> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    if (filters.cursor) params.set('cursor', filters.cursor);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<LeadListResponse>(
      `/admin/promotions/${encodeURIComponent(promoId)}/leads${query}`,
      undefined,
      API_TYPE,
    );
    if (!response.success) throw new Error('Failed to load leads');
    return { leads: response.data, cursor: response.cursor || null };
  }

  /** Admin: update a lead's status and/or notes. */
  async updateLead(
    promoId: string,
    leadSk: string,
    updates: { status?: LeadStatus; notes?: string },
  ): Promise<Lead> {
    const response = await apiClient.put<LeadUpdateResponse>(
      `/admin/promotions/${encodeURIComponent(promoId)}/leads/${encodeURIComponent(leadSk)}`,
      updates,
      undefined,
      API_TYPE,
    );
    if (!response.success) throw new Error('Failed to update lead');
    return response.data;
  }

  /**
   * Admin: download CSV export of all leads for a promotion.
   * Triggers a browser download via a synthetic <a> click.
   */
  async exportLeadsCsv(promoId: string): Promise<void> {
    const url = `/admin/promotions/${encodeURIComponent(promoId)}/leads/export`;
    // Use raw axios call so we can read the CSV body + filename from headers.
    // apiClient's .get returns response.data — for CSV we need full response, so go around it.
    const tokens = apiClient.getTokens();
    const apiBase = (await import('../config/apiConfig')).getApiEndpoints().api;
    const fullUrl = `${apiBase.replace(/\/$/, '')}${url}`;
    const headers: Record<string, string> = { Accept: 'text/csv' };
    if (tokens?.idToken) headers.Authorization = `Bearer ${tokens.idToken}`;
    const res = await fetch(fullUrl, { headers });
    if (!res.ok) {
      throw new Error(`Export failed: ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match?.[1] || `leads-${promoId}.csv`;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
}

const promotionsService = new PromotionsService();
export default promotionsService;
export { PromotionsService };
